import * as React from 'react';
import * as styles from './Faq.scss';
import { NavItem } from './nav-item/NavItem';
import { FaqItems } from './faq-items/FaqItems';
import { useTranslation } from 'react-i18next';
import { isEdge } from '@/utils/utils';
import { AppContextInstance } from '@/services/types';
import { AppContext, getTheme } from '@/context/AppContext';
import Contact, { ContactSize } from '@/containers/my-account/contact/Contact';
import { useIsMobileDeviceView } from '@/hooks/useIsMobileDeviceView';
import FaqSearch from './faq-search/FaqSearch';
import { useFAQApi } from '@/repositories/faq/faq-repository';

const Faq: React.FC = () => {
    const { t } = useTranslation();
    const { isInMobileView } = useIsMobileDeviceView();
    const context: AppContextInstance = React.useContext(AppContext);
    const questionsContainer = React.useRef<HTMLFormElement>(null);
    const [activeTab, setActiveTab] = React.useState<undefined | number>(undefined);
    const {
        value: { items: faqItems },
    } = useFAQApi();
    const [expandedAccordionId, setExpandedAccordionId] = React.useState<number | null>(null);

    const handleTabClick = (activeTabIndex: number, id?: number) => () => {
        setActiveTab(activeTabIndex);

        id ? setExpandedAccordionId(id) : null;

        if (!isInMobileView && questionsContainer.current) {
            const documentScrollTop =
                window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
            const top = questionsContainer.current.getBoundingClientRect().top + documentScrollTop - 140;
            isEdge()
                ? window.scrollTo(0, top)
                : window.scrollTo({
                      top,
                      behavior: 'smooth',
                  });
        }
    };

    const {
        Components: { Container },
    } = getTheme(context);

    return (
        <Container className={styles.faqContainer}>
            <div className="grid-row">
                <div className={`xs-12 l-8 ${styles.faqLeftSide}`}>
                    <h1 className={styles.header}>{t('translations.faq.header')}</h1>
                    <FaqSearch
                        handleTabClick={handleTabClick}
                        faqItems={faqItems}
                        placeholderLabel={t('translations.faq.input.searchPlaceholder')}
                    />
                    <p
                        dangerouslySetInnerHTML={{ __html: t('translations.faq.subheader') }}
                        className={styles.subheader}
                    />
                    <div className={styles.nav}>
                        {faqItems.map((tab, index: number) => (
                            <>
                                <NavItem
                                    key={`navItem${index}`}
                                    title={tab.title}
                                    iconPath={tab.iconPath}
                                    isActive={activeTab === index}
                                    onClick={handleTabClick(index)}
                                />
                                {isInMobileView && (
                                    <div className={styles.contentWrapper} ref={questionsContainer as any}>
                                        <FaqItems
                                            key={`faqItem${index}`}
                                            title={tab.title}
                                            items={tab.items}
                                            isActive={activeTab === index}
                                            expandedAccordionId={expandedAccordionId}
                                        />
                                    </div>
                                )}
                            </>
                        ))}
                    </div>
                    {!isInMobileView && (
                        <div className={styles.contentWrapper} ref={questionsContainer as any}>
                            {faqItems.map((tab, index: number) => (
                                <FaqItems
                                    key={`faqItem${index}`}
                                    title={tab.title}
                                    items={tab.items}
                                    isActive={activeTab === index}
                                    expandedAccordionId={expandedAccordionId}
                                />
                            ))}
                        </div>
                    )}
                </div>
                <Contact showContactInfo={false} classesWrapper={styles.contactWrapper} size={ContactSize.FAQ} />
            </div>
        </Container>
    );
};

export default Faq;
